import { AvailableViewers, RecognisedDataCatalogues, RecognisedDataObjects, PossibleEDMDTables } from 'common/enums/known-data-enums.js';

export type EdmNodeObject = { name: string; supportedViewers: AvailableViewers[]; type: PossibleEDMDTables; metadata?: Record<string, unknown> };
export type EdmNode = { name: string; objects?: EdmNodeObject[]; subNodes?: EdmNode[] };

// wrote it on Sindre request about having nested catalogues for structured data,
// our initial design support so no issues on db/server side
// issue is since we actually using subNodes now, we can't know how deep user would like to go
// hence recursion (written for future requests)
export const getAllEdmObjects = (nodes: EdmNode[]): EdmNodeObject[] =>
  nodes.flatMap((node) => [
    ...(node.objects ?? []),
    ...getAllEdmObjects(node.subNodes ?? []),
  ]);

export const EDM_NODES_DATA = {
  PROJECT_DATA: {
    name: RecognisedDataCatalogues.ProjectData,
    translationKey: 'edm_projectData',
    type: PossibleEDMDTables.ProjectData,
    objects: [
      { name: 'Project Data', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.ProjectData },
    ],
    subNodes: [],
  },
  LOCATION_DATA: {
    name: RecognisedDataCatalogues.CoordinateReferenceSystem,
    translationKey: 'emd_coordinateReferenceSystem',
    objects: [
      { name: RecognisedDataCatalogues.CoordinateReferenceSystem, supportedViewers: [AvailableViewers.Table], type: PossibleEDMDTables.CoordinateReferenceSystem },
    ],
    subNodes: [],
  },
  COORDINATE_REFERENCE_SYSTEM: {
    name: RecognisedDataCatalogues.WellboreWellheadPosition,
    translationKey: 'emd_wellboreWellHeadPosition',
    objects: [
      { name: RecognisedDataCatalogues.WellboreWellheadPosition, supportedViewers: [AvailableViewers.Table], type: PossibleEDMDTables.WellboreWellheadPosition },
    ],
    subNodes: [],
  },
  SITE_REFERENCE_SYSTEM: {
    translationKey: 'edm_siteReferenceSystem',
    name: RecognisedDataCatalogues.SiteReferenceSystem,
    objects: [
      { name: 'Site Reference System', supportedViewers: [AvailableViewers.Table], type: PossibleEDMDTables.SiteReferenceSystem },
    ],
  },
  PORE_PRESSURE: {
    translationKey: 'edm_porePressure',
    name: RecognisedDataCatalogues.PorePressure,
    objects: [
      { name: 'Pore Pressure (P10)', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.PorePressureP10 },
      { name: 'Pore Pressure (P50)', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.PorePressureP50 },
      { name: 'Pore Pressure (P90)', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.PorePressureP90 },
    ],
  },
  FRACTURE_PRESSURE: {
    translationKey: 'emd_fracturePressure',
    name: RecognisedDataCatalogues.FracturePressure,
    objects: [
      { name: 'Fracture Pressure (P10)', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.FracturePressureP10 },
      { name: 'Fracture Pressure (P50)', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.FracturePressureP50 },
      { name: 'Fracture Pressure (P90)', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.FracturePressureP90 },
    ],
  },
  COLLAPSE_PRESSURE: {
    translationKey: 'edm_collapsePressure',
    name: RecognisedDataCatalogues.CollapsePressure,
    objects: [
      { name: 'Collapse Pressure (P10)', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.CollapsePressureP10 },
      { name: 'Collapse Pressure (P50)', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.CollapsePressureP50 },
      { name: 'Collapse Pressure (P90)', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.CollapsePressureP90 },
    ],
  },
  MHS_PRESSURE: {
    translationKey: 'edm_MinimumHorizontalStressPressure',
    name: RecognisedDataCatalogues.MHSPressure,
    objects: [
      { name: 'Minimum Horizontal Stress Pressure (P10)', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.MinimumHorizontalStressPressureP10 },
      { name: 'Minimum Horizontal Stress Pressure (P50)', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.MinimumHorizontalStressPressureP50 },
      { name: 'Minimum Horizontal Stress Pressure (P90)', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.MinimumHorizontalStressPressureP90 },
    ],
  },
  GEOTHERMAL_TEMPERATURE: {
    translationKey: 'edm_geoThermalTemperature',
    name: RecognisedDataCatalogues.GeothermalTemperature,
    objects: [
      { name: 'Geothermal Temperature Definition', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.TemperatureDefinition },
      { name: 'Geothermal Temperature Listing', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.TemperatureListing },
    ],
  },
  TRAJECTORY_PLAN: {
    translationKey: 'edm_trajectoryPlan',
    name: RecognisedDataCatalogues.TrajectoryPlan,
    objects: [
      { name: 'Trajectory plan', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.TrajectoryPlan },
    ],
  },
  SURVEY_RUNS: {
    translationKey: 'edm_surveyRuns',
    name: RecognisedDataCatalogues.SurveyRuns,
    objects: [
      {
        name: 'Survey run', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.SurveyRun, metadata: { overlap: false },
      },
    ],
  },
  DEFINITIVE_SURVEY_DEFINITION: {
    translationKey: 'edm_surveyProgram',
    name: RecognisedDataCatalogues.SurveyProgram,
    objects: [
      { name: 'Survey Program', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.SurveyProgram },
    ],
  },
  DEFINITIVE_SURVEY_LIST: {
    translationKey: 'edm_definitiveSurveyList',
    name: RecognisedDataCatalogues.DefinitiveSurveyList,
    objects: [
      { name: 'Definitive Survey List', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.DefinitiveSurveyRun },
    ],
  },
  WELLBORE_POSITION_UNCERTAINTY: {
    translationKey: 'edm_wellborePositionUncertainty',
    name: RecognisedDataCatalogues.WellborePositionUncertainty,
    objects: [
      { name: 'Wellbore Position Uncertainty', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.PositionalUncertaintyList },
    ],
  },
  UNCERTAINTY_VECTORS: {
    translationKey: 'edm_uncertaintyVector',
    name: RecognisedDataCatalogues.UncertaintyVectors,
    objects: [
      { name: 'Uncertainty Vectors', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.UncertaintyVectors },
    ],
  },
  GEOMAGNETIC_PARAMETERS_DEFINITION: {
    translationKey: 'edm_geomagneticParametersDefinition',
    name: RecognisedDataCatalogues.GeomagneticParametersDefinition,
    objects: [
      { name: 'Geomagnetic Parameters Definition', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.GeomagneticParameters },
    ],
  },
  COVARIANCE_MATRIX_TOTAL: {
    translationKey: 'edm_covarianceMatrixTotal',
    name: RecognisedDataCatalogues.CovarianceMatrixTotal,
    subNodes: [
      {
        translationKey: 'edm_covarianceMatrixTotal1',
        name: RecognisedDataObjects.CovExternal,
        objects: [
          { name: RecognisedDataObjects.CovExternal, supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.CovarianceMatrix },
        ],
        subNodes: [],
      },
      {
        translationKey: 'edm_covarianceMatrixTotal2',
        name: RecognisedDataObjects.CovWelldesign,
        objects: [
          { name: RecognisedDataObjects.CovWelldesign, supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.CovarianceMatrix },
        ],
        subNodes: [],
      },
    ],
  },
  HOLE_SIZE: {
    translationKey: 'edm_holeSize',
    name: RecognisedDataCatalogues.HoleSize,
    objects: [
      { name: 'Hole Size', supportedViewers: [AvailableViewers.Table, AvailableViewers.Json], type: PossibleEDMDTables.HoleSize },
    ],
  },
}
